import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowLeft } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { getSolutionBySlug, solutionPages } from "../../data/pageConfig";

export default function SolutionPage() {
  const { solutionSlug, lang } = useParams();
  const { t } = useTranslation("solutions");
  const navigate = useNavigate();
  const solution = getSolutionBySlug(solutionSlug);

  if (!solution) {
    navigate(`/${lang || "en"}`, { replace: true });
    return null;
  }

  const Icon = solution.icon;
  const prefix = lang ? `/${lang}` : "";

  const problems = t(`${solution.key}.problems`, { returnObjects: true });
  const steps = t(`${solution.key}.steps`, { returnObjects: true });

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t(`${solution.key}.title`)} — TrackTeam`}
        description={t(`${solution.key}.description`)}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <Link to={`${prefix}`} className="inline-flex items-center gap-2 text-[#62329f] hover:underline mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            {t("backToHome")}
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${solution.gradient} flex items-center justify-center ${solution.shadowColor} shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
              {t(`${solution.key}.title`)}
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            {t(`${solution.key}.description`)}
          </p>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t(`${solution.key}.problemTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.isArray(problems) &&
              problems.map((prob, i) => (
                <div key={i} className="bg-red-50 rounded-2xl p-6 border border-red-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{prob.title}</h3>
                  <p className="text-gray-600">{prob.text}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* How TrackTeam Solves It */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t(`${solution.key}.solutionTitle`)}
          </h2>
          <div className="space-y-8">
            {Array.isArray(steps) &&
              steps.map((step, i) => (
                <div key={i} className="flex gap-6 items-start bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#62329f] flex items-center justify-center text-white font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Other Solutions */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t("otherSolutions")}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {solutionPages
              .filter((s) => s.slug !== solutionSlug)
              .map((s) => {
                const SIcon = s.icon;
                return (
                  <Link
                    key={s.slug}
                    to={`${prefix}/solutions/${s.slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 hover:border-[#62329f] hover:text-[#62329f] transition-colors text-sm font-medium"
                  >
                    <SIcon className="w-4 h-4" />
                    {t(`${s.key}.title`)}
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t(`${solution.key}.ctaTitle`)}</h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">{t(`${solution.key}.ctaSubtitle`)}</p>
          <div className="flex justify-center gap-4">
            <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
